import { v4 as uuidv4 } from 'uuid';

import config from '../config';
import { eventTracking } from '../util/api';
import { getAttribution } from '../util/consent/attribution';
import hasEffectiveConsent from '../util/consent/effectiveConsent';
import { collectTrackingIds } from '../util/consent/trackingIds';
import { logError } from '../util/log';

export class FacebookBaseFacebookEvent {
  static name = '';
  static eventData = {};
  static _explicitEventID = false;
  static params = {};
  static currentUserUUID = null;

  static setParam(key, value) {
    if (value != null) this.params[key] = value;
    return this;
  }

  static withCurrentUserUUID(uuid) {
    this.currentUserUUID = uuid || null;
    return this;
  }

  static withCustomName(customName) {
    this.name = customName;
    return this;
  }

  static withParams(params) {
    this.params = { ...this.params, ...params };
    return this;
  }

  static withEventID(eventID) {
    if (eventID) {
      this.eventData = { ...this.eventData, eventID };
      this._explicitEventID = true;
    }
    return this;
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static withContentCategory(contentCategory) {
    return this.setParam('content_category', contentCategory);
  }

  static withContentIds(contentIds) {
    return this.setParam('content_ids', contentIds);
  }

  static withContentName(contentName) {
    return this.setParam('content_name', contentName);
  }

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withNumItems(numItems) {
    return this.setParam('num_items', numItems);
  }

  static withContentType(contentType) {
    return this.setParam('content_type', contentType);
  }

  static withSearchString(searchString) {
    return this.setParam('search_string', searchString);
  }

  static async logEvent() {
    try {
      // Must be awaited: logFacebookEvent is async (permission check + dynamic
      // pixel import) — a fire-and-forget call let callers move on (and tests
      // assert) before the relay actually fired.
      await logFacebookEvent(this);
    } catch (e) {
      logError(e, 'facebook-event-tracking');
    }
  }

  static toJSON() {
    return {
      name: this.name,
      params: this.params,
      eventData: this.eventData,
    };
  }
}

export class FacebookScrollEvent extends FacebookBaseFacebookEvent {
  static name = 'Scroll';
}

export class FacebookPageViewEvent extends FacebookBaseFacebookEvent {
  static name = 'PageView';
}

export class FacebookAddPaymentInfoEvent extends FacebookBaseFacebookEvent {
  static name = 'AddPaymentInfo';
}

export class FacebookAddToWishlistEvent extends FacebookBaseFacebookEvent {
  static name = 'AddToWishlist';

  static withContentName(contentName) {
    return this.setParam('content_name', contentName);
  }

  static withContentCategory(contentCategory) {
    return this.setParam('content_category', contentCategory);
  }

  static withContentIds(contentIds) {
    return this.setParam('content_ids', contentIds);
  }

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', parseFloat(value));
  }
}

export class FacebookCompleteRegistrationEvent extends FacebookBaseFacebookEvent {
  static name = 'CompleteRegistration';
}

export class FacebookInitiateCheckoutEvent extends FacebookBaseFacebookEvent {
  static name = 'InitiateCheckout';

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }
}

export class FacebookBookingRequestEvent extends FacebookBaseFacebookEvent {
  static name = 'BookingRequest';
}

export class FacebookPurchaseEvent extends FacebookBaseFacebookEvent {
  static name = 'Purchase';

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withNumItems(numItems) {
    return this.setParam('num_items', numItems);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static async logEvent() {
    try {
      if (typeof this.params.value === 'undefined') {
        throw new Error('value is required');
      }
      if (!this.params.currency) {
        throw new Error('currency is required');
      }
      await super.logEvent();
    } catch (e) {
      logError(e, 'facebook-event-tracking');
    }
  }
}

export class FacebookSearchEvent extends FacebookBaseFacebookEvent {
  static name = 'Search';

  static withSearchString(searchString) {
    return this.setParam('search_string', searchString);
  }
}

export class FacebookViewContentEvent extends FacebookBaseFacebookEvent {
  static name = 'ViewContent';

  static withContentCategory(contentCategory) {
    return this.setParam('content_category', contentCategory);
  }

  static withContentName(contentName) {
    return this.setParam('content_name', contentName);
  }

  static withContentType(contentType) {
    return this.setParam('content_type', contentType);
  }

  static withContentIds(contentIds) {
    return this.setParam('content_ids', contentIds);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }
}

export class FacebookCustomEvent extends FacebookBaseFacebookEvent {
  static name = 'Custom';

  static withParams(params) {
    this.params = { ...this.params, ...params };
    return this;
  }

  static withCustomName(customName) {
    this.name = customName;
    return this;
  }
}

// params/eventData are static on FacebookBaseFacebookEvent and none of its ~11
// subclasses redeclare them, so ES statics are inherited (not copied) and every
// class shares the literal same objects. Without resetting after each fire,
// fields set by one event (e.g. ViewContent's contents/content_ids) leak into
// whatever unrelated event class fires next (e.g. Search, AddPaymentInfo). Must
// reset on the BASE class — `this.params = {}` from within a subclass only
// shadows the shared object for that one subclass, masking the bug rather than
// fixing it.
const resetSharedFacebookState = () => {
  FacebookBaseFacebookEvent.params = {};
  FacebookBaseFacebookEvent.eventData = {};
};

const logFacebookEvent = async (event) => {
  if (typeof window === 'undefined') return;

  // Snapshot then clear the explicit-id flag UNCONDITIONALLY, before any early
  // return — otherwise a consent-denied event leaves the flag (and its explicit
  // eventID) set on the class statics, and the next same-class event reuses it.
  const explicitEventID = event._explicitEventID;
  event._explicitEventID = false;

  const [marketing, statistics] = await Promise.all([
    hasEffectiveConsent('marketing'),
    hasEffectiveConsent('statistics'),
  ]);
  // The relay drives BOTH server sinks: Meta CAPI (marketing) and the GA4
  // Measurement Protocol mirror (statistics). Fire it whenever either applies —
  // gating on marketing alone left server-side GA4 dead for statistics-only users.
  if (!marketing && !statistics) {
    // Nothing fired, but this event's params/eventData were already built via
    // its with*() chain before logEvent() was called — reset now or the next,
    // unrelated event class inherits them.
    resetSharedFacebookState();
    return;
  }

  // Meta dedups Pixel vs CAPI via event_id — it must be unique per event, not
  // per class (the old static uuid was generated once at module load).
  if (!explicitEventID) {
    event.eventData = { ...event.eventData, eventID: uuidv4() };
  }

  try {
    // The client-side FB Pixel is marketing-only; skip its init/track without
    // marketing consent, but still relay so the GA4 mirror can fire.
    if (marketing) {
      const ReactPixel = await import('@bettercart/react-facebook-pixel');

      if (ReactPixel.default.init.length === 0) {
        ReactPixel.default.init(
          config.facebook.pixel.id || '',
          {
            ct: '',
            country: '',
            db: '',
            em: '',
            fn: '',
            ge: '',
            ln: '',
            ph: '',
            st: '',
            zp: '',
          },
          config.facebook.pixel.options,
        );
      }

      if (event.name === 'PageView') {
        ReactPixel.default.pageView();
      } else if (event.name === 'Custom') {
        ReactPixel.default.trackCustom(event.custom_name, event.params, event.eventData);
        event.name = event.custom_name;
      } else {
        ReactPixel.default.track(event.name, event.params, event.eventData);
      }
    }

    // CompleteRegistration is Pixel-only by design — not relayed server-side
    // (the server sign_up mirror would double-count the client registration).
    // The server would still mirror this as sign_up (FB_TO_GA4_EVENT), and
    // this return also skips Meta CAPI and the identity sync — keep it.
    if (event.name === 'CompleteRegistration') {
      // params/eventData already consumed by the Pixel track() call above (if
      // marketing consent was granted) — reset before returning either way.
      resetSharedFacebookState();
      return;
    }

    // Awaited: rejections must land in the catch below, not escape as an
    // unhandled promise rejection (logEvent's contract is a fully-awaited chain).
    await eventTracking({
      userId: event.currentUserUUID,
      event: event.toJSON(),
      url: window?.location?.href,
      consent: { statistics, marketing },
      ids: collectTrackingIds(),
      attribution: getAttribution(),
    });
    resetSharedFacebookState();
  } catch (e) {
    logError(e, 'facebook-event-tracking');
    resetSharedFacebookState();
  }
};

export const setFacebookUser = (currentUser) => {
  if (typeof window === 'undefined') return;

  hasEffectiveConsent('marketing')
    .then((permission) => {
      if (!permission) return;
      return import('@bettercart/react-facebook-pixel');
    })
    .then((ReactPixel) => {
      if (!ReactPixel) return;
      ReactPixel.default.init(
        config.facebook.pixel.id || '',
        {
          ct: '',
          country: '',
          db: '',
          em: currentUser.attributes.email,
          fn: currentUser.attributes.profile.firstName,
          ge: '',
          ln: currentUser.attributes.profile.lastName,
          ph: currentUser.attributes.profile.protectedData.phoneNumber,
          st: '',
          zp: '',
        },
        config.facebook.pixel.options,
      );
    });
};
